/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Service from '@ember/service';
import type { Block } from '@intercom/interblocks.ts';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { type BlockGroup } from 'embercom/components/knowledge-hub/content-editor/shared/suggestion-editor';
import type Model from '@ember-data/model';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type ArticleContent from 'embercom/models/articles/article-content';
import type ContentSnippet from 'embercom/models/content-service/content-snippet';
import { EntityType } from 'embercom/models/data/entity-types';
import { action } from '@ember/object';
import {
  ReviewType,
  type Suggestion,
} from 'embercom/models/content-service/content-review-request';

type SuggestionDetails = {
  suggestion?: Suggestion;
  reviewReason: string;
  suggestionCreatedAt: Date;
  contentReviewRequestId: string;
  reviewType: ReviewType;
};

export default class SuggestionEditorService extends Service {
  @tracked declare activeContent: LocalizedKnowledgeContent & Model;
  @tracked blockGroups: BlockGroup[] = [];
  @tracked blockGroupsForEditing: { [key: string]: Block[] } = {};

  private getJsonBlocks(activeContent: LocalizedKnowledgeContent & Model): Block[] {
    return activeContent.entityType === EntityType.ContentSnippet
      ? (activeContent as ContentSnippet).serializedBlocks
      : (activeContent as unknown as ArticleContent).jsonBlocks;
  }

  @action
  createBlockGroups(
    activeContent: LocalizedKnowledgeContent & Model,
    reviewRequests: ContentReviewRequest[],
  ): BlockGroup[] {
    let editedBlockCounter = 0;
    let uneditedBlockCounter = 0;
    let blockGroups: BlockGroup[] = [];
    let currentIndex = 0;
    let jsonBlocks = this.getJsonBlocks(activeContent);

    this.blockGroupsForEditing = {};

    let maybeSortedSuggestions = this.maybeSortedSuggestions(reviewRequests);

    for (let suggestionDetails of maybeSortedSuggestions) {
      if (suggestionDetails.reviewType === ReviewType.CREATE_CONTENT) {
        this.handleCreateContentSuggestion(blockGroups, suggestionDetails, jsonBlocks);
        // Set currentIndex to the end of the jsonBlocks array because we only have one suggestion for create content
        // This is cause no other suggestions will be added after this
        currentIndex = jsonBlocks.length + 1;
      } else {
        let result = this.handleEditContentSuggestion(
          blockGroups,
          suggestionDetails,
          jsonBlocks,
          currentIndex,
          uneditedBlockCounter,
          editedBlockCounter,
        );

        currentIndex = result.currentIndex;
        uneditedBlockCounter = result.uneditedBlockCounter;
        editedBlockCounter = result.editedBlockCounter;
      }
    }

    // Add any remaining unedited blocks after the last suggestion
    this.handleRemainingUneditedBlocks(blockGroups, jsonBlocks, currentIndex, uneditedBlockCounter);

    this.blockGroups = blockGroups;
    return blockGroups;
  }

  private maybeSortedSuggestions(reviewRequests: ContentReviewRequest[]): SuggestionDetails[] {
    let suggestionsWithReasons: SuggestionDetails[] = reviewRequests
      .map((request) => ({
        suggestion: request.get('suggestion') ?? undefined,
        reviewReason: request.get('reviewReason'),
        suggestionCreatedAt: request.get('createdAt'),
        contentReviewRequestId: request.get('id'),
        reviewType: request.get('reviewType'),
      }))
      .filter(({ suggestion }) => !isEmpty(suggestion));

    // Sort suggestions by start index to process them in order
    if (suggestionsWithReasons.length > 0) {
      return [...suggestionsWithReasons].sort(
        (a, b) => a.suggestion!.indices[0] - b.suggestion!.indices[0],
      );
    }
    return suggestionsWithReasons;
  }

  private handleCreateContentSuggestion(
    blockGroups: BlockGroup[],
    suggestionDetails: SuggestionDetails,
    jsonBlocks: Block[],
  ) {
    let indices: [number, number] = [0, 0];
    blockGroups.push({
      id: 'unedited-block-group-0',
      isEdited: false,
      blocks: jsonBlocks,
      indices,
      reviewReason: suggestionDetails.reviewReason,
      suggestionCreatedAt: suggestionDetails.suggestionCreatedAt,
      includedConversationIds: suggestionDetails.suggestion!.includedConversationIds,
      reviewType: suggestionDetails.reviewType,
    });
    this.blockGroupsForEditing[`${indices}`] = jsonBlocks;
  }

  private handleEditContentSuggestion(
    blockGroups: BlockGroup[],
    suggestionDetails: SuggestionDetails,
    jsonBlocks: Block[],
    currentIndex: number,
    uneditedBlockCounter: number,
    editedBlockCounter: number,
  ) {
    let [startIndex, endIndex] = suggestionDetails.suggestion!.indices;

    // Add unedited blocks before the suggestion if any
    if (currentIndex < startIndex) {
      let blocksForGroup = jsonBlocks.slice(currentIndex, startIndex);

      blockGroups.push({
        id: `unedited-block-group-${uneditedBlockCounter++}`,
        isEdited: false,
        blocks: blocksForGroup,
        blocksBefore: blocksForGroup,
        indices: [currentIndex, startIndex - 1],
      });
      this.blockGroupsForEditing[`${[currentIndex, startIndex - 1]}`] = blocksForGroup;
    }

    let blocksBefore = jsonBlocks.slice(startIndex, endIndex + 1);

    // Add the edited blocks from the suggestion
    blockGroups.push({
      isEdited: true,
      id: `edited-block-group-${editedBlockCounter++}`,
      blocks: suggestionDetails.suggestion!.editedBlocks,
      blocksBefore,
      indices: [startIndex, endIndex],
      reviewReason: suggestionDetails.reviewReason,
      suggestionCreatedAt: suggestionDetails.suggestionCreatedAt,
      includedConversationIds: suggestionDetails.suggestion!.includedConversationIds,
      contentReviewRequestId: suggestionDetails.contentReviewRequestId,
      reviewType: suggestionDetails.reviewType,
    });
    this.blockGroupsForEditing[`${[startIndex, endIndex]}`] =
      suggestionDetails.suggestion!.editedBlocks;

    currentIndex = endIndex + 1;

    return { currentIndex, uneditedBlockCounter, editedBlockCounter };
  }

  private handleRemainingUneditedBlocks(
    blockGroups: BlockGroup[],
    jsonBlocks: Block[],
    currentIndex: number,
    uneditedBlockCounter: number,
  ) {
    let blocksAfterSuggestions = jsonBlocks.slice(currentIndex);

    if (currentIndex < jsonBlocks.length) {
      blockGroups.push({
        isEdited: false,
        id: `unedited-block-group-${uneditedBlockCounter++}`,
        blocks: blocksAfterSuggestions,
        blocksBefore: blocksAfterSuggestions,
        indices: [currentIndex, jsonBlocks.length - 1],
      });
      this.blockGroupsForEditing[`${[currentIndex, jsonBlocks.length - 1]}`] =
        blocksAfterSuggestions;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    suggestionEditorService: SuggestionEditorService;
    'suggestion-editor-service': SuggestionEditorService;
  }
}
